import { RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import type { SubscriberListView, SubscriberView } from '@/types/api';

interface SubscribersTableProps {
  subscribers: SubscriberView[];
  lists: SubscriberListView[];
  busyId: string | null;
  loading: boolean;
  onAssign: (id: string, listId: string | null) => void;
  onReactivate: (id: string) => void;
  onDelete: (subscriber: SubscriberView) => void;
  /** Pass these to enable the row-selection checkbox column (bulk delete). */
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string, checked: boolean) => void;
  onToggleAll?: (checked: boolean) => void;
}

export function SubscribersTable({
  subscribers,
  lists,
  busyId,
  loading,
  onAssign,
  onReactivate,
  onDelete,
  selectedIds,
  onToggleSelect,
  onToggleAll,
}: SubscribersTableProps) {
  const selectable = typeof onToggleSelect === 'function';
  const colCount = selectable ? 7 : 6;
  const allSelected =
    subscribers.length > 0 && subscribers.every((s) => selectedIds?.has(s.id));
  const someSelected = subscribers.some((s) => selectedIds?.has(s.id));

  return (
    <div className="overflow-x-auto rounded-2xl border border-or-slate-200 bg-white">
      <table className="w-full text-left text-[13px]">
        <thead className="bg-or-slate-50 text-[11px] uppercase tracking-[0.08em] text-or-slate-500">
          <tr>
            {selectable ? (
              <th className="px-4 py-3">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  checked={allSelected}
                  onChange={(e) => onToggleAll?.(e.target.checked)}
                  className="h-4 w-4 rounded border-or-slate-300 text-or-orange-600"
                />
              </th>
            ) : null}
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Source</th>
            <th className="px-4 py-3">List</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading && subscribers.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="px-4 py-12 text-center text-or-slate-500">
                Loading…
              </td>
            </tr>
          ) : subscribers.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="px-4 py-12 text-center text-or-slate-500">
                No subscribers yet.
              </td>
            </tr>
          ) : (
            subscribers.map((sub) => {
              const name = `${sub.firstName ?? ''} ${sub.lastName ?? ''}`.trim();
              return (
                <tr key={sub.id} className="border-t border-or-slate-100 align-middle">
                  {selectable ? (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${sub.email}`}
                        checked={selectedIds?.has(sub.id) ?? false}
                        onChange={(e) => onToggleSelect?.(sub.id, e.target.checked)}
                        className="h-4 w-4 rounded border-or-slate-300 text-or-orange-600"
                      />
                    </td>
                  ) : null}
                  <td className="px-4 py-3 font-semibold text-or-slate-900">{sub.email}</td>
                  <td className="px-4 py-3 text-or-slate-700">{name || '—'}</td>
                  <td className="px-4 py-3">
                    <StatusChip status={sub.status} />
                  </td>
                  <td className="px-4 py-3 text-or-slate-500">{sourceLabel(sub.source)}</td>
                  <td className="px-4 py-3">
                    <select
                      value={sub.listId ?? ''}
                      disabled={busyId === sub.id}
                      onChange={(e) => onAssign(sub.id, e.target.value || null)}
                      className="h-8 max-w-[180px] rounded-lg border border-or-slate-200 bg-white px-2 text-[12px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
                    >
                      <option value="">Unassigned</option>
                      {lists.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {sub.status !== 'active' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onReactivate(sub.id)}
                          disabled={busyId === sub.id}
                        >
                          <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                          Reactivate
                        </Button>
                      ) : null}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onDelete(sub)}
                        disabled={busyId === sub.id}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function sourceLabel(source: string): string {
  if (source === 'account_sync') return 'Account';
  if (source === 'import') return 'Import';
  return 'Manual';
}

function StatusChip({ status }: { status: string }) {
  if (status === 'active') return <Chip variant="live">Active</Chip>;
  if (status === 'unsubscribed') return <Chip variant="default">Unsubscribed</Chip>;
  if (status === 'bounced') return <Chip variant="pending">Bounced</Chip>;
  if (status === 'complained') return <Chip variant="pending">Complained</Chip>;
  return <Chip variant="default">{status}</Chip>;
}
